import { memo } from 'react'

import { times } from 'lodash'

import { Divider } from '@mui/material'
import { AddOutlined, AssignmentOutlined, KeyboardArrowLeft } from '@mui/icons-material'

import { useDispatch, useSelector } from '@tabeeb/store'
import { Button, IconButton, Placeholder, Stack, Subheader1, useDialogState } from '@collaborate-health/uikit'
import { formTitleFormatter, sessionTitleFormatter } from '@tabeeb/modules/shared/utils/text'

import SwitchFormDialogContainer from '../../containers/SwitchFormDialogContainer'
import InfoMessageDialogContainer from '../../containers/InfoMessageDialogContainer'

import { openForm, openReadonlyForm, unsetCurrentForm } from '../../actions'
import {
  contentFormsLoadingState,
  getAreCompleteOrFeedbackInContentReviewButtonsVisible,
  getContentFormsForCurrentUser,
  getCurrentFormId,
  getCurrentFormName,
  getIsAddFormButtonVisible,
  getIsSendForInContentReviewButtonVisible,
} from '../../selectors'

import CurrentForm from '../CurrentForm'
import FormsListItem, { FormsListItemSkeleton } from '../FormsListItem'
import ExportFormButton from '../ExportFormButton'
import FormsNavigationMenu from '../FormsNavigationMenu'
import AttachFormsDialog from '../AttachFormsDialog'
import SendForInContentReviewButton from '../SendForInContentReviewButton'
import SendInContentReviewFeedbackButton from '../SendInContentReviewFeedbackButton'
import CompleteInContentReviewButton from '../CompleteInContentReviewButton'

export interface FormsProps {
  readonly?: boolean
}

const Forms: React.FC<FormsProps> = ({ readonly = false }) => {
  const dispatch = useDispatch()

  const [open, onOpen, onClose] = useDialogState()

  const { loading, loaded } = useSelector(contentFormsLoadingState)
  const forms = useSelector(getContentFormsForCurrentUser)
  const isEmpty = loaded && forms.length === 0

  const currentFormId = useSelector(getCurrentFormId)
  const currentFormName = useSelector(getCurrentFormName)

  const isAddFormButtonVisible = useSelector(getIsAddFormButtonVisible)

  const isInContentReviewActionsBarVisible = useSelector(
    (state) =>
      getIsSendForInContentReviewButtonVisible(state) || getAreCompleteOrFeedbackInContentReviewButtonsVisible(state)
  )

  const isFormSelected = Boolean(currentFormId)

  const onSelectForm = (formId: number) => {
    if (readonly) {
      dispatch(openReadonlyForm(formId))
    } else {
      dispatch(openForm(formId))
    }
  }

  const title = currentFormName ?? formTitleFormatter.format('Forms')

  return (
    <Stack bgcolor='background.paper' spacing={1} flex={1} paddingTop={1} height='100%' overflow='hidden'>
      <Stack
        minHeight={40}
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        spacing={1}
        paddingInline={1.5}
      >
        <Stack direction='row' alignItems='center' overflow='hidden' spacing={0.5}>
          {isFormSelected && (
            <IconButton size='small' onClick={() => dispatch(unsetCurrentForm())}>
              <KeyboardArrowLeft />
            </IconButton>
          )}
          <Subheader1 noWrap title={title}>
            {title}
          </Subheader1>
          {isFormSelected && <FormsNavigationMenu selectedFormId={currentFormId} onSelectForm={onSelectForm} />}
        </Stack>
        {!readonly && (
          <>
            {!isFormSelected && isAddFormButtonVisible && (
              <Button startIcon={<AddOutlined />} color='primary' size='small' onClick={onOpen}>
                {formTitleFormatter.format('Add form')}
              </Button>
            )}
            {isFormSelected && <ExportFormButton />}
          </>
        )}
      </Stack>

      {isEmpty && (
        <Placeholder
          icon={AssignmentOutlined}
          title={formTitleFormatter.format('No forms attached')}
          subtitle={formTitleFormatter.format(
            sessionTitleFormatter.format(`There are no forms attached to the session`)
          )}
        />
      )}

      {!isFormSelected && (
        <>
          <Stack flex={1} spacing={1} overflow='auto' p={1.5}>
            {forms.map((form) => (
              <FormsListItem key={form.Id} form={form} onSelect={onSelectForm} />
            ))}

            {loading && times(5, (index) => <FormsListItemSkeleton key={`skeleton-${index}`} />)}
          </Stack>

          {isInContentReviewActionsBarVisible && (
            <>
              <Divider />
              <Stack direction='row' justifyContent='space-evenly' gap={1} paddingInline={1.5} pb={1}>
                <SendForInContentReviewButton />
                <CompleteInContentReviewButton />
                <SendInContentReviewFeedbackButton />
              </Stack>
            </>
          )}
        </>
      )}

      {isFormSelected && <CurrentForm readonly={readonly} />}

      <InfoMessageDialogContainer />
      <SwitchFormDialogContainer />

      <AttachFormsDialog open={open} onClose={onClose} />
    </Stack>
  )
}

export default memo(Forms)

